/**
 * Department + Staff↔Department Membership API (EPIC A / A1)
 *
 * Backend endpoints (all scoped to the authenticated user's organisation,
 * gated server-side by MANAGE_DEPARTMENT / VIEW_DEPARTMENT):
 *
 *   Departments
 *     POST   /api/organisation/departments                 { name, code? } → dept
 *     GET    /api/organisation/departments?include_inactive=  → [{id,name,code,active,member_count}]
 *     GET    /api/organisation/departments/:id
 *     PATCH  /api/organisation/departments/:id              { name?, code? }
 *     DELETE /api/organisation/departments/:id              (soft-delete; warn member_count)
 *     POST   /api/organisation/departments/:id/reactivate   (deleted→active) → dept
 *
 *   Membership (many-to-many member ↔ department)
 *     POST   /api/organisation/departments/:id/members      { member_id } → assign (409 dup)
 *     DELETE /api/organisation/departments/:id/members/:member_id → unassign (204 if absent)
 *     GET    /api/organisation/departments/:id/members      → [{member_id,name,role}]
 *     GET    /api/organisation/members/:member_id/departments → [{dept_id,name}]
 *
 * Every function returns `{ success, data?, error? }`. Errors are already
 * translated to user-friendly strings via `buildApiError()`.
 *
 * Conventions cloned from procedureApi.js / doctorTeamApi.js (apiClient,
 * {success,data,error} contract, server `detail` verbatim).
 */

import apiClient from './apiClient';

// ── Contract normalizers (backend is the contract authority) ─────────
/**
 * Map a backend DepartmentResponse onto the internal dept shape the store
 * and components consume.
 *
 * Backend truth (app/schemas/department.py → DepartmentResponse):
 *   { dept_id, organization_id, name, code, status: 'active'|'deleted',
 *     created_at, updated_at, created_by_member_id }
 * — NO `id`, NO `active` boolean, NO `member_count` on list/detail rows.
 *
 * Internal shape (kept stable so callers don't churn):
 *   - `id`     ← dept_id   (also keep dept_id for membership writes)
 *   - `active` ← status === 'active'
 *   - `member_count` IS now returned by the backend on dept list rows (real
 *     integer, 0 is a real count). Passed through verbatim. The soft-delete
 *     endpoint also returns a real member_count — surfaced separately.
 *
 * @param {object} row backend department row
 * @returns {object} normalized dept
 */
function normalizeDept(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.dept_id ?? row.id,
    dept_id: row.dept_id ?? row.id,
    active: row.status ? row.status === 'active' : row.active !== false,
  };
}

/**
 * Map a backend membership edge (DepartmentMemberResponse) onto the internal
 * shape.
 *
 * dept→members rows now carry { member_id, name, role } (backend joins the
 * member's name/role) — passed through verbatim so the UI renders human names
 * instead of opaque ids. member→depts rows carry { dept_id, name } (and may
 * fall back to a join in the store if name is absent). Either way we just
 * spread the edge through with stable id fields.
 *
 * @param {object} row backend membership edge
 */
function normalizeEdge(row) {
  if (!row || typeof row !== 'object') return row;
  return { ...row };
}

// ── Local error translator ───────────────────────────────────────────
/**
 * Translate an axios error into a user-friendly string. Keeps the server's
 * `detail` message verbatim when available (FastAPI returns `{ detail: "..." }`
 * on 4xx / 422) and falls back to a category-specific default.
 *
 * @param {unknown} error    axios error
 * @param {string}  fallback default message when we can't do better
 * @returns {string}
 */
function buildApiError(error, fallback) {
  const data = error?.response?.data;
  const serverMsg = data?.detail ?? data?.message;
  if (serverMsg) {
    return typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg);
  }

  switch (error?.response?.status) {
    case 400: return 'Invalid data — please check the form and try again.';
    case 401: return 'Session expired. Please login again.';
    case 403: return 'You do not have permission to perform this action.';
    case 404: return 'The requested department could not be found.';
    case 409: return 'That department already exists. Pick a different name or code.';
    case 422: return 'Invalid data — please check the form and try again.';
    case 500: return 'Server error. Please try again in a moment.';
    default:  return fallback;
  }
}

// ══════════════════════════════════════════════════════════════════════
//  Departments
// ══════════════════════════════════════════════════════════════════════

/**
 * Create a department.
 *
 * POST /api/organisation/departments
 * @param {{ name: string, code?: string }} body
 * @returns {Promise<{success, data?, error?}>}
 */
export const createDept = async (body) => {
  try {
    const response = await apiClient.post('/api/organisation/departments', body);
    return { success: true, data: normalizeDept(response.data) };
  } catch (error) {
    console.error('[DepartmentAPI] create error:', error);
    return { success: false, error: buildApiError(error, 'Failed to create department.') };
  }
};

/**
 * List departments. Soft-deleted (inactive) rows are excluded server-side by
 * default; pass include_inactive to include them.
 *
 * GET /api/organisation/departments
 * @param {{ include_inactive?: boolean }} [params]
 * @returns {Promise<{success, data?: {departments: Array, count: number}, error?}>}
 */
export const listDepts = async ({ include_inactive } = {}) => {
  try {
    const params = {};
    if (include_inactive) params.include_inactive = true;

    const response = await apiClient.get('/api/organisation/departments', { params });
    // Backend returns a {departments,count} envelope; tolerate a bare array too.
    const rawDepts = Array.isArray(response.data)
      ? response.data
      : response.data?.departments || [];
    const departments = rawDepts.map(normalizeDept);
    return {
      success: true,
      data: {
        departments,
        count: response.data?.count ?? departments.length,
      },
    };
  } catch (error) {
    console.error('[DepartmentAPI] list error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load departments.') };
  }
};

/**
 * Fetch a single department.
 *
 * GET /api/organisation/departments/:id
 */
export const getDept = async (id) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/departments/${encodeURIComponent(id)}`
    );
    return { success: true, data: normalizeDept(response.data) };
  } catch (error) {
    console.error('[DepartmentAPI] get error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load department.') };
  }
};

/**
 * Partial update (rename / re-code). Membership survives (same id).
 *
 * PATCH /api/organisation/departments/:id
 * @param {string} id
 * @param {{ name?: string, code?: string }} body
 */
export const updateDept = async (id, body) => {
  try {
    const response = await apiClient.patch(
      `/api/organisation/departments/${encodeURIComponent(id)}`,
      body
    );
    return { success: true, data: normalizeDept(response.data) };
  } catch (error) {
    console.error('[DepartmentAPI] update error:', error);
    return { success: false, error: buildApiError(error, 'Failed to update department.') };
  }
};

/**
 * Soft-delete (deactivate). Memberships are kept; backend returns a warn
 * payload carrying the affected member_count.
 *
 * DELETE /api/organisation/departments/:id
 * @returns {Promise<{success, data?: {member_count?: number}, error?}>}
 */
export const softDeleteDept = async (id) => {
  try {
    const response = await apiClient.delete(
      `/api/organisation/departments/${encodeURIComponent(id)}`
    );
    return { success: true, data: response.data ?? null };
  } catch (error) {
    console.error('[DepartmentAPI] softDelete error:', error);
    return { success: false, error: buildApiError(error, 'Failed to deactivate department.') };
  }
};

/**
 * Reactivate (un-delete) a soft-deleted department. Flips status deleted→active;
 * memberships were kept on deactivate so they are restored as-is.
 *
 * POST /api/organisation/departments/:id/reactivate
 * @returns {Promise<{success, data?, error?}>} data is the normalized dept
 */
export const reactivateDept = async (id) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/departments/${encodeURIComponent(id)}/reactivate`
    );
    return { success: true, data: normalizeDept(response.data) };
  } catch (error) {
    console.error('[DepartmentAPI] reactivate error:', error);
    return { success: false, error: buildApiError(error, 'Failed to reactivate department.') };
  }
};

// ══════════════════════════════════════════════════════════════════════
//  Membership (member ↔ department)
// ══════════════════════════════════════════════════════════════════════

/**
 * Assign a member to a department. 409 if the link already exists.
 *
 * POST /api/organisation/departments/:id/members  { member_id }
 */
export const assignMember = async (deptId, memberId) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/departments/${encodeURIComponent(deptId)}/members`,
      { member_id: memberId }
    );
    return { success: true, data: response.data ?? null };
  } catch (error) {
    console.error('[DepartmentAPI] assignMember error:', error);
    return { success: false, error: buildApiError(error, 'Failed to assign member to department.') };
  }
};

/**
 * Unassign a member from a department. Idempotent — 204 even if the link
 * was already absent.
 *
 * DELETE /api/organisation/departments/:id/members/:member_id
 */
export const unassignMember = async (deptId, memberId) => {
  try {
    await apiClient.delete(
      `/api/organisation/departments/${encodeURIComponent(deptId)}/members/${encodeURIComponent(memberId)}`
    );
    return { success: true };
  } catch (error) {
    console.error('[DepartmentAPI] unassignMember error:', error);
    return { success: false, error: buildApiError(error, 'Failed to remove member from department.') };
  }
};

/**
 * List a department's members.
 *
 * GET /api/organisation/departments/:id/members → [{member_id,name,role}]
 * @returns {Promise<{success, data?: {members: Array, count: number}, error?}>}
 */
export const listDeptMembers = async (deptId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/departments/${encodeURIComponent(deptId)}/members`
    );
    const rawMembers = Array.isArray(response.data)
      ? response.data
      : response.data?.members || [];
    const members = rawMembers.map(normalizeEdge);
    return {
      success: true,
      data: { members, count: response.data?.count ?? members.length },
    };
  } catch (error) {
    console.error('[DepartmentAPI] listDeptMembers error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load department members.') };
  }
};

/**
 * List a member's departments (proves the M:N direction).
 *
 * GET /api/organisation/members/:member_id/departments → [{dept_id,name}]
 * @returns {Promise<{success, data?: {departments: Array, count: number}, error?}>}
 */
export const listMemberDepts = async (memberId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/members/${encodeURIComponent(memberId)}/departments`
    );
    const rawDepartments = Array.isArray(response.data)
      ? response.data
      : response.data?.departments || [];
    // These are membership edges ({dept_id, member_id, status,...}) — NOT full
    // dept rows; the backend does not embed the dept name here. The store joins
    // the name from its dept-list cache. Pass edges through normalized.
    const departments = rawDepartments.map(normalizeEdge);
    return {
      success: true,
      data: { departments, count: response.data?.count ?? departments.length },
    };
  } catch (error) {
    console.error('[DepartmentAPI] listMemberDepts error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load member departments.') };
  }
};

const departmentApi = {
  createDept,
  listDepts,
  getDept,
  updateDept,
  softDeleteDept,
  reactivateDept,
  assignMember,
  unassignMember,
  listDeptMembers,
  listMemberDepts,
};

export default departmentApi;
